<template>
  <div :class="$style.container">
    <!-- Filters -->
    <v-card :class="$style.headerCard">
      <v-row :class="$style.headerWrapper" dense>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="queryParams.search"
            hide-details
            density="compact"
            append-inner-icon="fas fa-solid fa-search"
            clearable
            :placeholder="globalTranslate('externalLmsRegistrations.searchPlaceholder')"
            @update:model-value="val => onSearchChange(val)"
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-text-field
            v-model="queryParams.startDate"
            hide-details
            density="compact"
            type="date"
            :label="globalTranslate('externalLmsRegistrations.startDate')"
            @update:model-value="onDateFilterChange"
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-text-field
            v-model="queryParams.endDate"
            hide-details
            density="compact"
            type="date"
            :label="globalTranslate('externalLmsRegistrations.endDate')"
            @update:model-value="onDateFilterChange"
          />
        </v-col>
        <v-col cols="12" md="4" :class="$style.actionColumn">
          <v-btn
            color="primary"
            :loading="isExporting"
            :disabled="isLoading"
            prepend-icon="fas fa-solid fa-file-excel"
            @click="exportRegistrations"
          >
            {{ globalTranslate('externalLmsRegistrations.exportExcel') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Data Table -->
    <app-data-table-server
      :headers="headers"
      :items="items"
      :total-result="totalResult"
      :is-items-loading="isLoading"
      :page="queryParams.page"
      :per-page="queryParams.perPage"
      :sort="currentSort"
      :total-pages="totalPages"
      :is-checkbox-shown="false"
      :is-expandable="false"
      :can-row-toggle="false"
      @on-page-change="onPageChange"
      @update:sort-by="onSortChange"
    >
      <template #r_num="{ item }">
        <div class="text-center">
          {{ item.raw.r_num }}
        </div>
      </template>

      <template #id="{ item }">
        <div class="text-nowrap">
          {{ item.raw.id }}
        </div>
      </template>

      <template #name="{ item }">
        <div class="text-nowrap">
          {{ item.raw.first_name }} {{ item.raw.last_name }}
        </div>
      </template>

      <template #client_key="{ item }">
        <code :class="$style.clientKeyCode">{{ item.raw.client_key }}</code>
      </template>

      <template #external_user_id="{ item }">
        <div :class="$style.externalUserId">
          {{ item.raw.external_user_id }}
        </div>
      </template>

      <template #training_status="{ item }">
        <div :class="$style.trainingStatusColumn">
          <div class="d-flex justify-space-between align-center">
            <span class="text-caption font-weight-bold">{{ Math.round(item.raw.progress || 0) }}%</span>
            <v-chip
              :color="getStatusColor(item.raw)"
              size="x-small"
              label
              class="text-uppercase"
            >
              {{ getStatusText(item.raw) }}
            </v-chip>
          </div>
        </div>
      </template>

      <template #create_date="{ item }">
        <div class="text-nowrap">
          {{ formatDate(item.raw.create_date) }}
        </div>
      </template>

      <template #edit_date="{ item }">
        <div class="text-nowrap">
          <template v-if="parseInt(item.raw.status_id, 10) === STATUS_COMPLETED_PASS">
            {{ formatDate(item.raw.edit_date) }}
          </template>
        </div>
      </template>
    </app-data-table-server>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.isShowing"
      location="top right"
      :timeout="5000"
    >
      <v-alert
        closable
        :type="snackbar.type"
        :color="snackbar.type"
        @click:close="snackbar.isShowing = false"
      >
        {{ snackbar.message }}
      </v-alert>
    </v-snackbar>
  </div>
</template>

<script setup>
import {
  ref, reactive, computed, onMounted,
} from 'vue';
import axios from 'axios';
import moment from 'moment';
import AppDataTableServer from '@pfsc/shared/components/app-data-table-server.vue';
import { globalTranslate } from '@pfsc/shared/locale/app.i18n';
import { debounce } from '@pfsc/core/utils/debounce';
import {
  STATUS_APPROVED,
  STATUS_COMPLETED_PASS,
  STATUS_COMPLETED_FAIL,
} from '@pfsc/core/utils/constants/course-info';

const items = ref([]);
const totalResult = ref(0);
const totalPages = ref(0);
const isLoading = ref(false);
const isExporting = ref(false);

const queryParams = reactive({
  page: 1,
  perPage: 25,
  sortBy: '',
  sortDirection: '',
  search: '',
  startDate: '',
  endDate: '',
});

const currentSort = computed(() => {
  if (!queryParams.sortBy) return [];
  return [{ key: queryParams.sortBy, order: queryParams.sortDirection }];
});

const headers = [
  {
    title: globalTranslate('externalLmsClientKeys.clientKey'), key: 'client_key', sortable: true,
  },
  {
    title: globalTranslate('externalLmsRegistrations.confirmation'), key: 'id', sortable: true,
  },
  {
    title: globalTranslate('externalLmsRegistrations.externalLearnerId'), key: 'external_user_id', sortable: true,
  },
  {
    title: globalTranslate('assignedCourseTable.studentName'), key: 'name', sortable: false,
  },
  {
    title: globalTranslate('externalLmsRegistrations.occurrence'), key: 'r_num', sortable: false, align: 'center',
  },
  {
    title: globalTranslate('assignedCourse.course'), key: 'course_name', sortable: false,
  },
  {
    title: globalTranslate('externalLmsRegistrations.trainingStatus'), key: 'training_status', sortable: false, minWidth: '176px',
  },
  {
    title: globalTranslate('externalLmsRegistrations.completedDate'), key: 'edit_date', sortable: false,
  },
  {
    title: globalTranslate('externalLmsClientKeys.createdDate'), key: 'create_date', sortable: true,
  },
];

// Snackbar
/** @type {import('vue').UnwrapRef<{isShowing: boolean, message: string, type: 'success' | 'error' | 'info' | 'warning'}>} */
const snackbar = reactive({
  isShowing: false,
  message: '',
  type: 'success',
});

/**
 * Show notification.
 * @param {string} message Message.
 * @param {'success'|'error'} type Type.
 */
const showNotification = (message, type = 'success') => {
  snackbar.message = message;
  snackbar.type = type;
  snackbar.isShowing = true;
};

const formatDate = date => (date ? moment(date).format('MM/DD/YYYY') : '-');

const getStatusText = item => {
  const statusId = parseInt(item.status_id, 10);
  const progress = parseFloat(item.progress || 0);

  if (statusId === STATUS_COMPLETED_PASS) return globalTranslate('externalLmsRegistrations.status.completedPassed');
  if (statusId === STATUS_COMPLETED_FAIL) return globalTranslate('externalLmsRegistrations.status.completedFailed');

  if (statusId === STATUS_APPROVED) {
    return progress > 0
      ? globalTranslate('externalLmsRegistrations.status.inProgress')
      : globalTranslate('externalLmsRegistrations.status.notStarted');
  }

  return '-';
};

const getStatusColor = item => {
  const statusId = parseInt(item.status_id, 10);
  const progress = parseFloat(item.progress || 0);

  if (statusId === STATUS_COMPLETED_PASS) return 'success';
  if (statusId === STATUS_COMPLETED_FAIL) return 'error';

  if (statusId === STATUS_APPROVED) {
    return progress > 0 ? 'info' : 'grey';
  }

  return 'grey';
};

const fetchRegistrations = async () => {
  isLoading.value = true;
  try {
    const response = await axios.get('/api/v2/admin/external-lms/registrations', {
      params: queryParams,
    });

    items.value = response.data.data;
    totalResult.value = response.data.total;
    totalPages.value = response.data.totalPages;
  } catch (e) {
    showNotification('Failed to fetch registrations.', 'error');
  } finally {
    isLoading.value = false;
  }
};

const exportRegistrations = async () => {
  // Only validate when a date filter is being used. With no dates, export whatever is showing.
  if (queryParams.startDate || queryParams.endDate) {
    if (!queryParams.startDate) {
      showNotification(globalTranslate('externalLmsRegistrations.exportStartDateRequired'), 'error');
      return;
    }

    if (queryParams.endDate && moment(queryParams.endDate).isBefore(moment(queryParams.startDate), 'day')) {
      showNotification(globalTranslate('externalLmsRegistrations.exportEndDateBeforeStart'), 'error');
      return;
    }
  }

  isExporting.value = true;
  try {
    const response = await axios.get('/api/v2/admin/external-lms/registrations/export', {
      params: queryParams,
    });

    window.location.href = response.data.url;
    showNotification(globalTranslate('externalLmsRegistrations.exportSuccess'));
  } catch (e) {
    showNotification(globalTranslate('externalLmsRegistrations.exportFailed'), 'error');
  } finally {
    isExporting.value = false;
  }
};

const onSearchChange = debounce(() => {
  queryParams.page = 1;
  fetchRegistrations();
}, 500);

const onDateFilterChange = () => {
  queryParams.page = 1;
  fetchRegistrations();
};

const onPageChange = options => {
  queryParams.page = options.page;
  queryParams.perPage = options.perPage;
  fetchRegistrations();
};

const onSortChange = sortValue => {
  if (sortValue && sortValue.length > 0) {
    queryParams.sortBy = sortValue[0].key;
    queryParams.sortDirection = sortValue[0].order;
  } else {
    queryParams.sortBy = '';
    queryParams.sortDirection = '';
  }
  queryParams.page = 1;
  fetchRegistrations();
};

onMounted(() => {
  fetchRegistrations();
});
</script>

<style module>
.container {
  position: relative;
  z-index: 0;
}

.headerCard {
  margin-bottom: 20px;
  padding: 16px;
}

.headerWrapper {
  align-items: center;
}

.actionColumn {
  display: flex;
  justify-content: flex-end;
}

.clientKeyCode {
  padding: 2px 6px;
  background-color: #f5f5f5;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.9em;
}

.externalUserId {
  font-family: monospace;
  color: #666;
}

.trainingStatusColumn {
  min-width: 150px;
  display: flex;
  flex-direction: column;
}

.container :global(.v-table__wrapper table .v-data-table__thead tr td),
.container :global(.v-table__wrapper table .v-data-table__tbody tr td) {
  padding: 0 12px;
}
</style>
